/**
 * useSimulatorHint — request a coaching hint from the edge function.
 *
 * Score penalty is server-tracked. The hook just returns the hint text +
 * cumulative penalty so the UI can render an inline coach bubble.
 */
import { useCallback, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { requestHint, type HintResponse } from "../api/client";
import { toast } from "@/lib/toast";
import { isNoConfiguredProviderError, showNoConfiguredProviderToast } from "@/lib/edgeFnError";

export interface UseSimulatorHintReturn {
  isLoading: boolean;
  ask: (sessionId: string) => Promise<HintResponse | null>;
  lastHint: HintResponse | null;
}

export function useSimulatorHint(): UseSimulatorHintReturn {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [lastHint, setLastHint] = useState<HintResponse | null>(null);

  const ask = useCallback(
    async (sessionId: string): Promise<HintResponse | null> => {
      if (isLoading) return null;
      setIsLoading(true);
      try {
        const token = await session?.getToken();
        if (!token) {
          toast.error("Sessão expirou. Faça login de novo.");
          return null;
        }
        const res = await requestHint(sessionId, token);
        setLastHint(res);
        return res;
      } catch (err) {
        if (isNoConfiguredProviderError(err)) {
          showNoConfiguredProviderToast();
          return null;
        }
        const e = err as Error & { code?: string; status?: number };
        if (e.code === "hint_cap_reached") {
          toast.error("Limite de dicas dessa sessão atingido.");
        } else {
          toast.error(e.message ?? "Erro ao pedir dica.");
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, session],
  );

  return { isLoading, ask, lastHint };
}
